import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Loader2, Paperclip, Plus, Sparkles, X } from "lucide-react";
import { cn } from "@/infrastructure/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { FileTypeIcon } from "@/shared/components/files/FileTypeIcon";
import { detectDocumentKind } from "@/shared/utils/documentFileTypes";
import type { ChatAttachment } from "../types/chatBase";
import { ATTACHMENT_ACCEPT, attachmentBadge, formatAttachmentSize } from "../utils/chatAttachments";

interface AttachButtonProps {
  onPick: (files: File[]) => void;
  disabled?: boolean;
  className?: string;
}

/** O botão + do chat (AC18): escolhe arquivos para anexar à próxima mensagem. */
export function AttachButton({ onPick, disabled, className }: AttachButtonProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            aria-label={t("playground.attachments.add")}
            className={cn(
              "flex items-center justify-center text-muted-foreground transition-colors",
              "hover:bg-primary/10 hover:text-primary disabled:opacity-30 disabled:hover:bg-transparent",
              className,
            )}
          >
            <Plus className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">{t("playground.attachments.add")}</TooltipContent>
      </Tooltip>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        className="hidden"
        data-testid="chat-attachment-input"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (files.length > 0) onPick(files);
        }}
      />
    </TooltipProvider>
  );
}

interface PendingAttachmentsProps {
  files: File[];
  onRemove: (index: number) => void;
  disabled?: boolean;
}

/** Os anexos escolhidos e ainda não enviados, dentro da caixa de mensagem. */
export function PendingAttachments({ files, onRemove, disabled }: PendingAttachmentsProps) {
  const { t } = useTranslation();
  if (files.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 pb-2" data-testid="pending-attachments">
      {files.map((file, index) => (
        <span
          key={`${file.name}-${index}`}
          className="inline-flex items-center gap-2 rounded-lg border border-primary/15 bg-primary/5 py-1 pl-1 pr-1.5 text-xs"
        >
          <FileTypeIcon kind={detectDocumentKind(file.name)} tile="sm" />
          <span className="max-w-[200px] truncate font-medium text-foreground">{file.name}</span>
          <span className="text-muted-foreground">{formatAttachmentSize(file.size)}</span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onRemove(index)}
            aria-label={t("playground.attachments.remove", { name: file.name })}
            className="rounded-md p-0.5 text-muted-foreground hover:bg-primary/10 hover:text-primary disabled:opacity-30"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

/**
 * Os anexos de uma mensagem já enviada, dentro do balão dela: o mesmo ícone por formato
 * da tela Files, o nome e o tamanho. Clicar abre o arquivo (link temporário). Não diz se
 * entraram na base: quem decide é a conversa. Avisa quando o agente só leu o começo do
 * arquivo ou não achou texto nele (AC20).
 */
const BADGE_TEXT = {
  unreadable: ["unreadable", "unreadableHint"],
  partial: ["truncated", "truncatedHint"],
  summarizing: ["summarizing", "summarizingHint"],
  summarized: ["summarized", "summarizedHint"],
  summaryFailed: ["summaryFailed", "summaryFailedHint"],
} as const;

/** Situação do texto do anexo para o assistente, ao lado do tamanho (AB#87121, AC22/AC23). */
function AttachmentBadge({ attachment }: { attachment: ChatAttachment }) {
  const { t } = useTranslation();
  const badge = attachmentBadge(attachment);
  if (!badge) return null;
  const [label, hint] = BADGE_TEXT[badge];
  const tone =
    badge === "summarized"
      ? "text-primary"
      : badge === "summarizing"
        ? "text-muted-foreground"
        : "text-amber-600 dark:text-amber-400";
  return (
    <span className={cn("inline-flex items-center gap-1", tone)} title={t(`playground.attachments.${hint}`)}>
      {" · "}
      {badge === "summarizing" && <Loader2 className="h-3 w-3 animate-spin" />}
      {badge === "summarized" && <Sparkles className="h-3 w-3" />}
      {t(`playground.attachments.${label}`)}
    </span>
  );
}

export function MessageAttachments({
  attachments,
  onOpen,
}: {
  attachments?: ChatAttachment[];
  /** Abre o arquivo. Sem token — a mensagem ainda subindo — o anexo não abre. */
  onOpen?: (attachment: ChatAttachment) => void;
}) {
  const { t } = useTranslation();
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mb-2 flex flex-col gap-1.5" data-testid="message-attachments">
      {attachments.map((a, index) => {
        const canOpen = !!onOpen && !!a.upload_token;
        const format = (a.format ?? a.name.split(".").pop() ?? "").toUpperCase();
        const kind = detectDocumentKind(a.name);
        return (
          <button
            key={a.upload_token ?? `${a.name}-${index}`}
            type="button"
            disabled={!canOpen}
            onClick={() => canOpen && onOpen!(a)}
            title={canOpen ? t("playground.attachments.open", { name: a.name }) : a.name}
            className={cn(
              "group/att flex w-full min-w-[220px] max-w-[320px] items-center gap-2.5 rounded-lg border border-primary/15 bg-background/80 p-1.5 pr-2.5 text-left text-xs",
              canOpen ? "cursor-pointer transition-colors hover:border-primary/40 hover:bg-primary/5" : "cursor-default",
            )}
          >
            {kind ? (
              <FileTypeIcon kind={kind} tile="md" />
            ) : (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
                <Paperclip className="h-5 w-5 text-primary" />
              </span>
            )}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-medium text-foreground">{a.name}</span>
              <span className="text-muted-foreground">
                {[format, formatAttachmentSize(a.size_bytes)].filter(Boolean).join(" · ")}
                <AttachmentBadge attachment={a} />
              </span>
            </span>
            {canOpen && (
              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60 transition-colors group-hover/att:text-primary" />
            )}
          </button>
        );
      })}
    </div>
  );
}
